import type { Ring, Vec2 } from '../../geo/types';
import { pointInRing, ringBounds } from '../../geo/polygon';

/* The ground under a footprint is read at its corners, along its edges and over its inside at
   this spacing: between two quays it rises to a ridge no corner stands on. */
const GROUND_SAMPLE_M = 3;

/* Every height the ground is read at under a footprint. */
function groundUnder(
  footprint: Ring,
  elevation: (p: Vec2) => number,
  visit: (height: number) => void
): void {
  footprint.forEach((a, i) => {
    visit(elevation(a));
    const b = footprint[(i + 1) % footprint.length]!;
    const steps = Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / GROUND_SAMPLE_M);
    for (let k = 1; k < steps; k++) {
      const t = k / steps;
      visit(elevation([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]));
    }
  });
  // A house's inside lies within a sample of its edges; a landmark's court does not.
  const bounds = ringBounds(footprint);
  if (
    bounds.maxX - bounds.minX <= 2 * GROUND_SAMPLE_M ||
    bounds.maxY - bounds.minY <= 2 * GROUND_SAMPLE_M
  )
    return;
  for (let y = bounds.minY + GROUND_SAMPLE_M / 2; y < bounds.maxY; y += GROUND_SAMPLE_M)
    for (let x = bounds.minX + GROUND_SAMPLE_M / 2; x < bounds.maxX; x += GROUND_SAMPLE_M)
      if (pointInRing([x, y], footprint)) visit(elevation([x, y]));
}

/** The highest ground under a footprint, which a level building stands on. */
export function foundationHeight(footprint: Ring, elevation: (p: Vec2) => number): number {
  let height = -Infinity;
  groundUnder(footprint, elevation, (ground) => {
    height = Math.max(height, ground);
  });
  return height;
}

/** A house's front on its street: the middle of its front line, the way along it, the way out
 *  from it, and its width (lotFrame.ts). */
export interface HouseFront {
  readonly frontMid: Vec2;
  readonly right: Vec2;
  readonly facing: Vec2;
  readonly width: number;
}

/* The ground along a front is read this often, and a step keeps this much under the ground. */
const FRONT_SAMPLE_M = 0.5;
const FOOT_COVER_M = 0.03;

function alongFront(
  front: HouseFront,
  out: number,
  beyond: number,
  elevation: (p: Vec2) => number,
  visit: (height: number) => void
): void {
  const half = front.width / 2 + beyond;
  const steps = Math.max(1, Math.ceil((2 * half) / FRONT_SAMPLE_M));
  for (let k = 0; k <= steps; k++) {
    const along = -half + (2 * half * k) / steps;
    visit(
      elevation([
        front.frontMid[0] + front.right[0] * along + front.facing[0] * out,
        front.frontMid[1] + front.right[1] * along + front.facing[1] * out,
      ])
    );
  }
}

/**
 * The floor a house stands on: the street's level along its front, at the front's highest point.
 * A house of 1538 opened one step above its street (docs/plans/houses.md, "Stoepen"), so its
 * threshold follows the street and not the ground behind, which may rise under the house and
 * bury the foot of its back wall. Where the street slopes along the front the higher end sets the
 * floor, so no part of the front and no door stands in the ground.
 */
export function houseFloorHeight(front: HouseFront, elevation: (p: Vec2) => number): number {
  let height = -Infinity;
  alongFront(front, 0, 0, elevation, (ground) => {
    height = Math.max(height, ground);
  });
  return height;
}

/**
 * How far below the floor a stoep reaching `reach` out from the front must be built to stand on
 * the street: to the lowest ground under it and round its corners, and a little into it. Zero on
 * level ground.
 */
export function stoepFoot(
  front: HouseFront,
  floor: number,
  reach: number,
  elevation: (p: Vec2) => number
): number {
  let lowest = Infinity;
  for (const out of [0, reach])
    alongFront(front, out, reach, elevation, (ground) => {
      lowest = Math.min(lowest, ground);
    });
  return floor - lowest > 0.001 ? floor - lowest + FOOT_COVER_M : 0;
}

/**
 * How far under its floor a level building's own walls reach where it is given no foundation
 * (mesh/footedBuilder.ts): to the lowest ground under these rings, and a little into it.
 */
export function footDepth(
  rings: readonly Ring[],
  elevation: (p: Vec2) => number,
  floor: number
): number {
  let lowest = floor;
  for (const ring of rings)
    groundUnder(ring, elevation, (ground) => {
      lowest = Math.min(lowest, ground);
    });
  return floor - lowest > 0.001 ? floor - lowest + FOOT_COVER_M : 0;
}
